//
// Imports
//

import * as BrowserUtilities from "@donutteam/browser-utilities";
import { Notice } from "@donutteam/document-builder-components";

import { RequestBody as CreateUploadSessionRequestBody, ResponseBodySchema as CreateUploadSessionResponseBodySchema } from "../../../api/routes/v6/release-branch-version-upload-sessions/create.schemas.js";

import { ResponseBodySchema as UploadChunkResponseBodySchema } from "../../../api/routes/v6/release-branch-version-upload-sessions/upload-chunk.schemas.js";

//
// Local Functions
//

type GetElementsResult =
{
	component: HTMLElement;
	fileInput: HTMLInputElement;
	form: HTMLFormElement;
	noticeContainer: HTMLElement;
	status: HTMLElement;
	statusIcon: HTMLElement;
	statusPrimaryText: HTMLElement;
	statusSecondaryText: HTMLElement;
	titleInput: HTMLInputElement;
}

function getElements(createReleaseBranchVersionFileForm: HTMLElement): GetElementsResult
{
	const form = BrowserUtilities.ElementClientLib.getElementOrThrow<HTMLFormElement>(createReleaseBranchVersionFileForm, "form");

	const titleInput = BrowserUtilities.ElementClientLib.getElementOrThrow<HTMLInputElement>(form, "input[name=title]");

	const fileInput = BrowserUtilities.ElementClientLib.getElementOrThrow<HTMLInputElement>(form, "input[name=file]");

	const noticeContainer = BrowserUtilities.ElementClientLib.getElementOrThrow(createReleaseBranchVersionFileForm, ".notice-container");

	const status = BrowserUtilities.ElementClientLib.getElementOrThrow(createReleaseBranchVersionFileForm, ".status");

	const statusIcon = BrowserUtilities.ElementClientLib.getElementOrThrow(status, ".icon");

	const statusPrimaryText = BrowserUtilities.ElementClientLib.getElementOrThrow(status, ".primary-text");

	const statusSecondaryText = BrowserUtilities.ElementClientLib.getElementOrThrow(status, ".secondary-text");

	return {
		component: createReleaseBranchVersionFileForm,
		fileInput,
		form,
		noticeContainer,
		status,
		statusIcon,
		statusPrimaryText,
		statusSecondaryText,
		titleInput,
	};
}

type GetParametersResult = GetParametersResultFailure | GetParametersResultSuccess

type GetParametersResultFailure =
{
	success: false;
	error: string;
}

type GetParametersResultSuccess =
{
	success: true;
	parameters:
	{
		apiUrl: string;
		file: File;
		releaseBranchVersion_id: number;
		title: string;
	};
}

function getParameters(elements: GetElementsResult): GetParametersResult
{
	const apiUrl = elements.component.dataset["apiUrl"];

	if (apiUrl == null)
	{
		return {
			success: false,
			error: "No API url.",
		};
	}

	const file = elements.fileInput.files?.[0];

	if (file == null)
	{
		return {
			success: false,
			error: "No file.",
		};
	}

	const releaseBranchVersion_id = parseInt(elements.component.dataset["releaseBranchVersionId"] as string);

	if (isNaN(releaseBranchVersion_id))
	{
		return {
			success: false,
			error: "Missing or invalid release branch version ID.",
		};
	}

	const title = elements.titleInput.value?.trim();

	if (title == null)
	{
		return {
			success: false,
			error: "Missing title.",
		};
	}
	else if (title.length == 0)
	{
		return {
			success: false,
			error: "You must specify a title.",
		};
	}

	return {
		success: true,
		parameters:
		{
			apiUrl,
			file,
			releaseBranchVersion_id,
			title,
		},
	};
}

function splitFileIntoChunks(file: File, chunkSize: number): Blob[]
{
	const fileSize = file.size;

	const chunks: Blob[] = [];

	let offset = 0;

	while (offset < fileSize)
	{
		const chunk = file.slice(offset, offset + chunkSize);

		chunks.push(chunk);

		offset += chunkSize;
	}

	return chunks;
}

type SetStatusOptions =
{
	iconName: string;
	primaryText: string;
	secondaryText: string;
}

function setStatus(elements: GetElementsResult, options: SetStatusOptions)
{
	elements.statusIcon.className = "icon " + options.iconName;

	elements.statusPrimaryText.innerText = options.primaryText;

	elements.statusSecondaryText.innerText = options.secondaryText;
}

type StartUploadSessionResult = StartUploadSessionResultError | StartUploadSessionResultSuccess;

type StartUploadSessionResultError =
{
	success: false;
	error: string;
}

type StartUploadSessionResultSuccess =
{
	success: true;
	releaseBranchVersionUploadSession_id: number;
}

async function startUploadSession(parameters: GetParametersResultSuccess["parameters"], totalChunkCount : number): Promise<StartUploadSessionResult>
{
	try
	{
		const requestBody: CreateUploadSessionRequestBody =
		{
			releaseBranchVersion_id: parameters.releaseBranchVersion_id,
			title: parameters.title,
			totalChunkCount,
		};

		const response = await fetch(parameters.apiUrl + "/v6/release-branch-version-upload-sessions/create",
			{
				credentials: "include",
				method: "POST",
				headers:
				{
					"Content-Type": "application/json",
				},
				body: JSON.stringify(requestBody),
			});

		const responseJson = await response.json();

		const createReleaseBranchVersionUploadSessionResponse = CreateUploadSessionResponseBodySchema.parse(responseJson);

		if (!createReleaseBranchVersionUploadSessionResponse.success)
		{
			return {
				success: false,
				error: createReleaseBranchVersionUploadSessionResponse.errors[0]?.message ?? "Unknown error.",
			};
		}

		return {
			success: true,
			releaseBranchVersionUploadSession_id: createReleaseBranchVersionUploadSessionResponse.releaseBranchVersionUploadSession_id,
		};
	}
	catch (error)
	{
		console.error("[CreateReleaseBranchVersionFileForm] Error while starting upload session:", error);

		return {
			success: false,
			error: "An error occurred while contacting the API. It may be temporarily unavailable.",
		};
	}
}

type UploadChunkOptions =
{
	elements : GetElementsResult;
	parameters : GetParametersResultSuccess["parameters"];

	chunk: Blob;
	chunkIndex: number;
	chunkProgress: number[];
	releaseBranchVersionUploadSession_id : number;
	totalChunkCount: number;
}

type UploadChunkResult = UploadChunkResultFailure | UploadChunkResultSuccess;

type UploadChunkResultFailure =
{
	success: false;
	error: string;
}

type UploadChunkResultSuccess =
{
	success: true;
}

async function uploadChunk(options: UploadChunkOptions): Promise<UploadChunkResult>
{
	//
	// Create Form Data
	//

	const body = new FormData();

	body.append("chunkIndex", options.chunkIndex.toString());

	body.append("releaseBranchVersionUploadSession_id", options.releaseBranchVersionUploadSession_id.toString());

	body.append("chunkFile", options.chunk, "chunkFile");

	//
	// Create XMLHttpRequest
	//

	const xhr = new XMLHttpRequest();

	xhr.open("POST", options.parameters.apiUrl + "/v6/release-branch-version-upload-sessions/upload-chunk", true);

	xhr.withCredentials = true;

	xhr.upload.onprogress =
		(event) =>
		{
			options.chunkProgress[options.chunkIndex] = (event.loaded / event.total) * 100;

			const totalPercentComplete = options.chunkProgress.reduce((sum, chunkProgress) => sum + chunkProgress, 0) / options.totalChunkCount;

			setStatus(options.elements,
				{
					iconName: "fa-solid fa-upload fa-bounce",
					primaryText: totalPercentComplete.toFixed(2) + "%",
					secondaryText: "Your file is being uploaded. Please don't close this page.",
				});
		};

	//
	// Send Request
	//

	xhr.send(body);

	//
	// Wait for Response
	//

	try
	{
		await new Promise(
			(resolve, reject) =>
			{
				xhr.onload =
					() =>
					{
						resolve(true);
					};

				xhr.onerror =
					() =>
					{
						reject();
					};
			});

		const uploadChunkResponseBody = UploadChunkResponseBodySchema.parse(JSON.parse(xhr.responseText));

		if (!uploadChunkResponseBody.success)
		{
			console.error("[CreateReleaseBranchVersionFileForm] Failure response:", uploadChunkResponseBody);

			return {
				success: false,
				error: uploadChunkResponseBody.errors[0]?.message ?? "Unknown error.",
			};
		}

		return {
			success: true,
		};
	}
	catch (error)
	{
		console.error("[CreateReleaseBranchVersionFileForm] Error uploading chunk:", error);

		return {
			success: false,
			error: "An error occurred while uploading the chunk. The API may be temporarily unavailable.",
		};
	}
}

type SubmitResult = SubmitResultFailure | SubmitResultSuccess

type SubmitResultFailure =
{
	success : false;
	error : string;
}

type SubmitResultSuccess =
{
	success : true;
}

async function submit(elements: GetElementsResult) : Promise<SubmitResult>
{
	try
	{
		//
		// Disable Form
		//

		elements.form.setAttribute("disabled", "");
		elements.form.style.display = "none";

		elements.status.classList.add("show");

		//
		// Get Parameters
		//

		const getParametersResult = getParameters(elements);

		if (!getParametersResult.success)
		{
			return {
				success: false,
				error: getParametersResult.error,
			};
		}

		const parameters = getParametersResult.parameters;

		//
		// Chunkify File
		//

		const fiveMegabytes = 10 * 1024 * 1024;

		const chunks = splitFileIntoChunks(parameters.file, fiveMegabytes);

		//
		// Start Upload Session
		//

		setStatus(elements,
			{
				iconName: "fa-solid fa-spinner fa-spin",
				primaryText: "Starting upload...",
				secondaryText: "This may take a moment. Please don't close this page.",
			});

		const startUploadSessionResult = await startUploadSession(parameters, chunks.length);

		if (!startUploadSessionResult.success)
		{
			return {
				success: false,
				error: startUploadSessionResult.error,
			};
		}

		const chunkProgress = new Array(chunks.length).fill(0) as number[];

		for (const [ chunkIndex, chunk ] of chunks.entries())
		{
			const uploadChunkResult = await uploadChunk(
				{
					chunk,
					chunkIndex,
					chunkProgress,
					elements,
					parameters,
					releaseBranchVersionUploadSession_id: startUploadSessionResult.releaseBranchVersionUploadSession_id,
					totalChunkCount: chunks.length,
				});

			if (!uploadChunkResult.success)
			{
				// TODO: Issue a request to the API to cancel the session

				return {
					success: false,
					error: uploadChunkResult.error,
				};
			}
		}

		setStatus(elements,
			{
				iconName: "fa-solid fa-check",
				primaryText: "Upload complete",
				secondaryText: "Your upload may take a little while to process before it's available. Don't worry, we'll email you when it's done.",
			});

		return {
			success: true,
		};
	}
	catch (error)
	{
		console.error("[CreateReleaseBranchVersionFileForm] An error occurred:", error);

		elements.form.removeAttribute("disabled");
		elements.form.style.display = "block";
		elements.status.classList.remove("show");

		return {
			success: false,
			error: "Unknown error. See console for details.",
		};
	}
}

//
// Component
//

export function initialiseCreateReleaseBranchVersionFileForm(createReleaseBranchVersionFileForm: HTMLElement)
{
	const elements = getElements(createReleaseBranchVersionFileForm);

	elements.form.addEventListener("submit",
		async (event) =>
		{
			//
			// Prevent Default
			//

			event.preventDefault();

			//
			// Clear Notice Container
			//

			elements.noticeContainer.classList.remove("show");

			while (elements.noticeContainer.firstChild != null)
			{
				elements.noticeContainer.firstChild.remove();
			}

			//
			// Submit Form
			//

			const submitResult = await submit(elements);

			// TODO: Show the user the error, if there was one
			console.log("[CreateReleaseBranchVersionFileForm] Handle submit result:", submitResult);

			if (!submitResult.success)
			{
				const notice = Notice(
					{
						type: "warning",
						message: submitResult.error,
					}).renderToHTMLElement();

				elements.noticeContainer.classList.add("show");
				elements.noticeContainer.appendChild(notice);
			}
		});
		
	createReleaseBranchVersionFileForm.classList.add("initialised");
}

export function initialiseCreateReleaseBranchVersionFileForms()
{
	const createReleaseBranchVersionFileForms = document.querySelectorAll<HTMLElement>(".component-create-release-branch-version-file-form:not(.initialised)");

	console.log("[CreateReleaseBranchVersionFileForm] Initialising " + createReleaseBranchVersionFileForms.length + " instances...");

	for (const createReleaseBranchVersionFileForm of createReleaseBranchVersionFileForms)
	{
		try
		{
			initialiseCreateReleaseBranchVersionFileForm(createReleaseBranchVersionFileForm);
		}
		catch (error)
		{
			console.error("[CreateReleaseBranchVersionFileForm] Error initialising:", createReleaseBranchVersionFileForm, error);
		}
	}
}